/**
 * An 80-bit UUID (10 bytes), the frontend twin of the backend's
 * `internal/uuid_extensions/uuid80` package. Method names follow the Go ones
 * (`New`, `ToBase64`, `FromBase64`, `Bytes`, `String`) in camelCase.
 *
 * Its wire form is base64url without padding: 14 characters drawn from
 * `A–Z a–z 0–9 - _`. That alphabet has no `{` or `}`.
 */

const BYTE_LENGTH = 10;

/** The length of `toBase64()`'s output. */
export const BASE64_LENGTH = 14;

/** Any base64url-encoded UUID80. */
export const BASE64_RE = /^[A-Za-z0-9_-]{14}$/;

function encodeBase64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export class UUID80 {
  private constructor(private readonly raw: Uint8Array) {}

  /** A new random UUID80, like `uuid80.New()`. */
  static new(): UUID80 {
    const bytes = new Uint8Array(BYTE_LENGTH);
    crypto.getRandomValues(bytes);
    return new UUID80(bytes);
  }

  /** Decode a base64url UUID80, like `uuid80.FromBase64`. Throws if invalid. */
  static fromBase64(encoded: string): UUID80 {
    if (!BASE64_RE.test(encoded)) {
      throw new Error(`Not a base64url UUID80: "${encoded}"`);
    }
    const binary = atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
    return new UUID80(Uint8Array.from(binary, c => c.charCodeAt(0)));
  }

  /** The base64url encoding without padding, like `ToBase64`. */
  toBase64(): string {
    return encodeBase64Url(this.raw);
  }

  /** A copy of the 10 raw bytes, like `Bytes`. */
  bytes(): Uint8Array {
    return Uint8Array.from(this.raw);
  }

  /** Same as `toBase64()`, like `String`. */
  toString(): string {
    return this.toBase64();
  }
}

/** The zero UUID80, like `uuid80.Nil`. */
export const Nil = UUID80.fromBase64('AAAAAAAAAAAAAA');
